import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRelativeTime } from '@/lib/date';
import type { NotificationDto } from '@/lib/notifications';
import { NotificationTitleBody } from './NotificationTitleBody';
import { CATEGORY_ICON } from './notification-helpers';
import {
  useMarkAllReadMutation,
  useMarkNotificationReadMutation,
  useRecentNotificationsQuery,
  useUnreadCountQuery,
} from './queries';

type Props = { onClose: () => void };

/**
 * Content of the bell popover: a compact recent-notifications list, a
 * "Mark all read" action, and a "See all" link to the full feed. Mounts only
 * while the popover is open (Radix unmounts closed content), so the recent
 * query is always enabled here.
 */
export function NotificationDropdown({ onClose }: Props) {
  const query = useRecentNotificationsQuery(true);
  const markRead = useMarkNotificationReadMutation();
  const markAll = useMarkAllReadMutation();
  const unread = useUnreadCountQuery().data?.count ?? 0;

  const items = query.data?.pages[0]?.data ?? [];

  return (
    <div className="flex w-[360px] max-w-[calc(100vw-2rem)] flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
        <span className="text-sm font-semibold text-foreground">
          Notifications
        </span>
        <button
          type="button"
          className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
          disabled={unread === 0 || markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          Mark all read
        </button>
      </div>

      <div className="max-h-[min(60vh,420px)] overflow-y-auto py-1">
        {query.isLoading ? (
          <div className="flex flex-col gap-1 p-2" role="status" aria-label="Loading notifications">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : query.isError ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              Couldn&apos;t load notifications.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            You&apos;re all caught up.
          </p>
        ) : (
          <ul>
            {items.map((n) => (
              <DropdownRow
                key={n.id}
                notification={n}
                onMarkRead={() => markRead.mutate(n.id)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-border px-3 py-2">
        <Link
          to="/notifications"
          onClick={onClose}
          className="block rounded-md px-2 py-1.5 text-center text-[13px] font-medium text-brand hover:bg-accent"
        >
          See all notifications
        </Link>
      </div>
    </div>
  );
}

function DropdownRow({
  notification,
  onMarkRead,
}: {
  notification: NotificationDto;
  onMarkRead: () => void;
}) {
  const unread = notification.readAt === null;
  const Icon = notification.category
    ? CATEGORY_ICON[notification.category]
    : CATEGORY_ICON.SYSTEM;

  const content = (
    <>
      <span
        aria-hidden
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
      >
        <Icon size={13} />
      </span>
      <NotificationTitleBody
        className="flex-1"
        title={notification.title}
        body={notification.body}
        unread={unread}
        severity={notification.severity}
        emailAccountId={notification.emailAccountDeletedAt ? null : notification.emailAccountId}
      />
      <time
        dateTime={notification.createdAt}
        className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
      >
        {formatRelativeTime(notification.createdAt, { variant: 'feed' })}
      </time>
    </>
  );

  // Read rows render as a plain, non-interactive row (not a disabled
  // <button>) — a disabled button is unfocusable/untabbable, which made
  // every read notification unreachable via keyboard in the popover.
  if (!unread) {
    return (
      <li>
        <div className="flex w-full items-start gap-2.5 px-3 py-2 text-left">
          {content}
        </div>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={onMarkRead}
        className="flex w-full cursor-pointer items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-accent"
        aria-label={`Mark "${notification.title}" as read`}
      >
        {content}
      </button>
    </li>
  );
}
